import React, { useState, useEffect } from 'react';
import { TrendingUp, Clock, Award, User, Edit3, FileText, CheckCircle, Home, ArrowLeft, BookOpen, WifiOff, Settings, AlertCircle, Mail, RefreshCw, X } from 'lucide-react';
import { auth } from '../firebase';
import { useUserStats } from '../hooks/useUserStats';
import { useTranslation } from 'react-i18next';
import enhancedUserService from '../services/enhancedUserService';
import adminService from '../services/adminService';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';
import { sendVerificationEmail } from '../utils/authUtils';

// Design tokens, human-shaped, neuro-aesthetic
const TEAL = '#006D77';
const SAGE = '#83C5BE';
const CORAL = '#E29578';
const TEXT_PRIMARY = '#121212';
const TEXT_NEUTRAL = '#636E72';
const BORDER_CARD = '#E1E4E8';
const SHADOW_BENTO = '0 4px 20px rgba(0,0,0,0.05)';

const ProfilePage = ({ onNavigate }) => {
  const { t } = useTranslation();
  const currentUser = auth.currentUser;
  const { userStats, loading } = useUserStats();
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isEmailVerified, setIsEmailVerified] = useState(Boolean(currentUser?.emailVerified));
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [isRefreshingVerification, setIsRefreshingVerification] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('idle');
  const [verificationError, setVerificationError] = useState('');

  const isAdmin = adminService.isAdmin();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    setIsEmailVerified(Boolean(currentUser?.emailVerified));
  }, [currentUser?.emailVerified]);

  // Extract user info from Firebase Auth and UserStats
  const user = {
    name: currentUser?.displayName || 'User',
    email: currentUser?.email || 'user@example.com',
    avatar: currentUser?.photoURL || `https://placehold.co/128x128/006D77/ffffff?text=${currentUser?.displayName?.split(' ').map(n => n[0]).join('') || 'U'}`,
    since: currentUser?.metadata?.creationTime ?
      new Date(currentUser.metadata.creationTime).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long'
      }) : t('profile.unknown'),
  };

  const profileStats = userStats?.profile?.stats || {};
  const exercises = profileStats.practiceSessionsCompleted || 0;
  const gradePct = profileStats.totalQuestionsAttempted > 0
    ? Math.round((profileStats.totalCorrectAnswers / profileStats.totalQuestionsAttempted) * 100) : 0;
  const studyHours = Math.round((profileStats.totalStudyTimeMinutes || 0) / 60);
  const streakDays = profileStats.longestStreak || 0;

  // Stats with progress for circular rings (0–1). Goals: 20 exercises, 100% grade, 5h study, 7-day streak
  const stats = [
    { label: t('profile.stats.completedExercises'), value: exercises, icon: FileText, progress: Math.min(exercises / 20, 1) },
    { label: t('profile.stats.averageGrade'), value: `${gradePct}%`, icon: TrendingUp, progress: gradePct / 100 },
    { label: t('profile.stats.totalStudyTime'), value: `${studyHours} ${t('profile.stats.hours')}`, icon: Clock, progress: Math.min((profileStats.totalStudyTimeMinutes || 0) / 300, 1) },
    { label: t('profile.stats.longestStreak'), value: `${streakDays} ${t('profile.stats.days')}`, icon: Award, progress: Math.min(streakDays / 7, 1) },
  ];

  // Normalize Firestore Timestamp to ms (Firestore returns { toDate, seconds } not a plain Date)
  const getStartTimeMs = (startTime) => {
    if (startTime == null) return null;
    if (typeof startTime.toDate === 'function') return startTime.toDate().getTime();
    if (typeof startTime.seconds === 'number') return startTime.seconds * 1000;
    const d = new Date(startTime);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
  };

  const rawSessions = userStats?.recentSessions?.slice(0, 2) ?? [];
  const hasActivities = rawSessions.length > 0;
  const recentActivities = hasActivities
    ? rawSessions.map(session => {
        const startMs = getStartTimeMs(session.startTime);
        const daysAgo = startMs != null ? Math.floor((Date.now() - startMs) / (1000 * 60 * 60 * 24)) : null;
        const dateLabel = daysAgo != null
          ? (daysAgo <= 0 ? t('profile.activities.recently') : t('profile.activities.daysAgo', { count: daysAgo }))
          : t('profile.activities.recently');
        return {
          title: session.title || t('profile.activities.unknown'),
          description: session.type || t('profile.activities.general'),
          status: session.completed ? t('profile.activities.completed') : t('profile.activities.inProgress'),
          date: dateLabel,
          icon: session.type === 'practice' ? FileText : session.type === 'exam' ? Award : session.type === 'vocabulary' ? CheckCircle : User,
        };
      })
    : [];

  const handleEditProfile = () => {
    if (onNavigate) onNavigate('/profile/edit');
  };

  const handleResetOnboarding = async () => {
    if (!isAdmin) return;
    const confirmed = window.confirm('Reset onboarding and personalized plan for this account? This is mainly for admin testing.');
    if (!confirmed) return;
    try {
      const userId = currentUser?.uid;
      if (!userId) return;
      await enhancedUserService.saveOnboardingComplete(userId, {
        examProvider: null,
        level: null,
        examName: null,
        testDate: null,
        weakestSubject: null,
        focusAreas: [],
        purpose: null,
        placementLevel: null,
        placementScore: null,
        placementAnswers: [],
      });
      // Clear local exam + plan and onboarding cache so RequireOnboarding + /onboarding run again
      localStorage.removeItem(`personalizedPlan_${userId}`);
      localStorage.removeItem('personalizedPlan');
      localStorage.removeItem(`selectedExamData_${userId}`);
      localStorage.removeItem('selectedExamData');
      localStorage.removeItem(`onboardingComplete_${userId}`);
      sessionStorage.removeItem('selectedExamData');
      alert('Onboarding has been reset. You will be redirected to onboarding.');
      if (onNavigate) onNavigate('/onboarding');
    } catch (error) {
      console.error('Error resetting onboarding:', error);
      alert('Failed to reset onboarding. Check console for details.');
    }
  };

  const openVerificationModal = () => {
    setVerificationError('');
    setVerificationStatus('idle');
    setIsVerificationModalOpen(true);
  };

  const closeVerificationModal = () => {
    setIsVerificationModalOpen(false);
  };

  const handleSendVerificationFromModal = async () => {
    if (!auth.currentUser || isSendingVerification) {
      return;
    }

    setIsSendingVerification(true);
    setVerificationError('');
    setVerificationStatus('idle');

    try {
      const result = await sendVerificationEmail(auth.currentUser);
      if (!result?.success) {
        throw new Error(result?.error || t('profile.emailVerification.modal.errorSend'));
      }
      setVerificationStatus('sent');
    } catch (error) {
      setVerificationStatus('error');
      setVerificationError(error.message || t('profile.emailVerification.modal.errorSend'));
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleRefreshVerificationFromModal = async () => {
    if (!auth.currentUser || isRefreshingVerification) {
      return;
    }

    setIsRefreshingVerification(true);
    setVerificationError('');

    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        setIsEmailVerified(true);
        setVerificationStatus('verified');
        setTimeout(() => {
          setIsVerificationModalOpen(false);
        }, 600);
      } else {
        setVerificationStatus('error');
        setVerificationError(t('profile.emailVerification.modal.errorNotVerifiedYet'));
      }
    } catch (error) {
      setVerificationStatus('error');
      setVerificationError(error.message || t('profile.emailVerification.modal.errorRefresh'));
    } finally {
      setIsRefreshingVerification(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse max-w-7xl mx-auto pt-2 sm:pt-4 px-4 sm:px-6 lg:px-8 pb-24 sm:pb-8">
        <div className="h-6 sm:h-8 rounded w-1/3 mb-4 pb-3 border-b" style={{ borderColor: BORDER_CARD }}></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="h-48 sm:h-56 lg:h-64 rounded-2xl bg-white border opacity-80" style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}></div>
          <div className="lg:col-span-2 space-y-4">
            <div className="h-28 sm:h-32 rounded-2xl bg-white border" style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}></div>
            <div className="h-24 sm:h-28 rounded-2xl bg-white border" style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in min-h-full safe-area-inset pb-24 sm:pb-0" style={{ backgroundColor: '#fafafa' }}>
      <div className="max-w-7xl mx-auto pt-2 sm:pt-4 pb-4 sm:pb-8 lg:pb-12 px-4 sm:px-6 lg:px-8">
        {/* Sticky header: one title + offline indicator (8pt grid) */}
        <div className="sticky top-0 z-10 -mx-4 px-4 sm:-mx-6 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-6 bg-[#fafafa] border-b flex items-center justify-between gap-4" style={{ borderColor: BORDER_CARD }}>
          <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-relaxed" style={{ color: TEXT_PRIMARY }}>
            {t('profile.title')}
          </h1>
          {!isOnline && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white border text-xs font-medium shrink-0" style={{ borderColor: BORDER_CARD, color: TEXT_NEUTRAL }}>
              <WifiOff className="w-3.5 h-3.5" style={{ color: CORAL }} />
              Offline
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Bento: Profile card, 8pt grid, thumb-zone CTA */}
          <div className="space-y-4 sm:space-y-6">
            <div className="rounded-2xl bg-white border p-4 sm:p-6" style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}>
              <div className="text-center">
                <img
                  className="mx-auto h-20 w-20 sm:h-24 sm:w-24 lg:h-32 lg:w-32 rounded-full border-2 object-cover"
                  style={{ borderColor: TEAL }}
                  src={user.avatar}
                  alt=""
                />
                <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2">
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold leading-relaxed" style={{ color: TEXT_PRIMARY }}>{user.name}</h2>
                  {isEmailVerified ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold"
                      style={{ backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', color: '#065F46' }}
                    >
                      <CheckCircle className="w-3 h-3" />
                      {t('profile.emailVerification.verifiedShort')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={openVerificationModal}
                      className="inline-flex min-h-[36px] items-center gap-1 rounded-full border border-[#FDBA74] bg-[#FFF7ED] px-2.5 py-1 text-[11px] font-semibold text-[#9A3412] transition-colors hover:bg-[#FFEFD9]"
                    >
                      <AlertCircle className="w-3 h-3" />
                      {t('profile.emailVerification.unverifiedShort')}
                    </button>
                  )}
                </div>
                <p className="text-base truncate max-w-full px-2 leading-relaxed mt-0.5" style={{ color: TEXT_NEUTRAL }}>{user.email}</p>
                <p className="text-sm mt-2 sm:mt-3 leading-relaxed" style={{ color: TEXT_NEUTRAL }}>{t('profile.memberSince')} {user.since}</p>
                <button
                  onClick={handleEditProfile}
                  className="mt-4 sm:mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 min-h-[48px] rounded-2xl font-semibold border-2 transition-transform active:scale-95 touch-manipulation"
                  style={{ borderColor: BORDER_CARD, color: TEAL }}
                >
                  <Edit3 className="w-4 h-4 flex-shrink-0" />
                  <span className="text-base">{t('profile.editProfile')}</span>
                </button>
              </div>
            </div>

          </div>

          {/* Right: Stats (progress rings) + Activities (bento, empty state) */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 leading-relaxed" style={{ color: TEXT_PRIMARY }}>{t('profile.stats.title')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  const circumference = 2 * Math.PI * 18;
                  const strokeDash = stat.progress * circumference;
                  return (
                    <div
                      key={index}
                      className="rounded-2xl bg-white border p-4 text-center transition-transform active:scale-95 hover:scale-[1.02] touch-manipulation"
                      style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}
                    >
                      <div className="relative w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-2 sm:mb-3 flex items-center justify-center">
                        <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 44 44">
                          <circle cx="22" cy="22" r="18" fill="none" strokeWidth="3" style={{ stroke: BORDER_CARD }} />
                          <circle cx="22" cy="22" r="18" fill="none" strokeWidth="3" strokeLinecap="round" style={{ stroke: TEAL }} strokeDasharray={circumference} strokeDashoffset={circumference - strokeDash} />
                        </svg>
                        <span className="relative flex items-center justify-center text-[#121212]">
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: TEAL }} />
                        </span>
                      </div>
                      <p className="text-lg sm:text-xl font-bold leading-tight" style={{ color: TEXT_PRIMARY }}>{stat.value}</p>
                      <p className="text-xs mt-0.5 line-clamp-2 leading-relaxed" style={{ color: TEXT_NEUTRAL }}>{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 leading-relaxed" style={{ color: TEXT_PRIMARY }}>{t('profile.activities.title')}</h3>
              <div className="rounded-2xl bg-white border p-4 sm:p-6" style={{ borderColor: BORDER_CARD, boxShadow: SHADOW_BENTO }}>
                {!hasActivities ? (
                  <div className="text-center py-6 sm:py-8 px-4">
                    <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${SAGE}30` }}>
                      <Clock className="w-7 h-7" style={{ color: SAGE }} />
                    </div>
                    <p className="text-base font-semibold leading-relaxed mb-2" style={{ color: TEXT_PRIMARY }}>{t('profile.activities.noActivities')}</p>
                    <p className="text-sm leading-relaxed mb-4 max-w-xs mx-auto" style={{ color: TEXT_NEUTRAL }}>{t('profile.activities.startFirst')}</p>
                    <button
                      type="button"
                      onClick={() => onNavigate && onNavigate('/practice')}
                      className="min-h-[48px] px-6 py-3 rounded-2xl font-semibold text-white transition-transform active:scale-95 touch-manipulation"
                      style={{ backgroundColor: TEAL }}
                    >
                      Start a 10‑minute session
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentActivities.map((activity, index) => {
                      const ActivityIcon = activity.icon;
                      return (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${TEAL}20`, color: TEAL }}>
                          <ActivityIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-grow min-w-0">
                          <p className="font-semibold text-base truncate leading-relaxed" style={{ color: TEXT_PRIMARY }}>{activity.title}</p>
                          <p className="text-sm capitalize leading-relaxed" style={{ color: TEXT_NEUTRAL }}>{activity.description}</p>
                          <p className="text-sm font-medium mt-0.5" style={{ color: TEXT_NEUTRAL }}>{activity.status}</p>
                        </div>
                        <p className="text-xs flex-shrink-0 leading-relaxed" style={{ color: TEXT_NEUTRAL }}>{activity.date}</p>
                      </div>
                    ); })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isVerificationModalOpen && !isEmailVerified && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={closeVerificationModal}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
              className="relative w-full max-w-md rounded-3xl border-[3px] border-black bg-white p-6 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]"
              role="dialog"
              aria-modal="true"
              aria-label={t('profile.emailVerification.modal.title')}
            >
              <button
                type="button"
                onClick={closeVerificationModal}
                className="absolute right-4 top-4 inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-black/60 transition-colors hover:bg-black/5"
                aria-label={t('profile.emailVerification.modal.close')}
              >
                <X className="w-4 h-4" />
              </button>

              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <AlertCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-black" style={{ color: TEXT_PRIMARY }}>
                    {t('profile.emailVerification.modal.title')}
                  </h3>
                  <p className="text-sm" style={{ color: TEXT_NEUTRAL }}>
                    {t('profile.emailVerification.modal.subtitle')}
                  </p>
                </div>
              </div>

              <p className="mb-4 text-sm leading-relaxed" style={{ color: TEXT_NEUTRAL }}>
                {t('profile.emailVerification.modal.description', { email: user.email })}
              </p>

              {verificationStatus === 'sent' && (
                <div className="mb-3 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                  {t('profile.emailVerification.modal.sent')}
                </div>
              )}

              {verificationStatus === 'verified' && (
                <div className="mb-3 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                  {t('profile.emailVerification.modal.verified')}
                </div>
              )}

              {verificationStatus === 'error' && verificationError && (
                <div className="mb-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                  {verificationError}
                </div>
              )}

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleSendVerificationFromModal}
                  disabled={isSendingVerification}
                  className="inline-flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-black px-4 py-3 text-sm font-black text-black transition-colors hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Mail className="w-4 h-4" />
                  {isSendingVerification
                    ? t('profile.emailVerification.modal.sending')
                    : t('profile.emailVerification.modal.sendButton')}
                </button>
                <button
                  type="button"
                  onClick={handleRefreshVerificationFromModal}
                  disabled={isRefreshingVerification}
                  className="inline-flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-black px-4 py-3 text-sm font-black text-white transition-colors hover:bg-[#3A86FF] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshingVerification ? 'animate-spin' : ''}`} />
                  {isRefreshingVerification
                    ? t('profile.emailVerification.modal.refreshing')
                    : t('profile.emailVerification.modal.refreshButton')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <GrammarBottomNav
        buttons={[
          { id: 'home', icon: Home, label: t('nav.home', { defaultValue: 'Home' }), onClick: () => onNavigate && onNavigate('/dashboard'), color: TEAL },
          { id: 'back', icon: ArrowLeft, label: t('nav.back', { defaultValue: 'Back' }), onClick: () => onNavigate && onNavigate('/dashboard'), color: TEAL },
          { id: 'practice', icon: BookOpen, label: t('nav.practice', { defaultValue: 'Practice' }), onClick: () => onNavigate && onNavigate('/practice'), color: TEAL }
        ]}
      />
    </div>
  );
};

export default ProfilePage;